import React, { useState, useEffect } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { financeiroApi } from '../../../services/api';
import type { TipoTransacao } from '../../../types';

interface Subcategoria {
  id: string;
  nome: string;
}

interface CategoriaFormProps {
  mode: 'create' | 'edit';
}

const CategoriaForm: React.FC<CategoriaFormProps> = ({ mode }) => {
  const navigate = useNavigate();
  const { id } = useParams<{ id: string }>();
  const isEdit = mode === 'edit';

  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [errors, setErrors] = useState<Record<string, string>>({});

  // Lista de todas as subcategorias disponíveis
  const [todasSubcategorias, setTodasSubcategorias] = useState<Subcategoria[]>([]);

  // Subcategorias selecionadas para esta categoria
  const [subcategoriasSelecionadas, setSubcategoriasSelecionadas] = useState<Set<string>>(new Set());

  // Modal para adicionar subcategorias
  const [showAdicionarModal, setShowAdicionarModal] = useState(false);

  // Modal para criar nova subcategoria
  const [showCriarSubcategoriaModal, setShowCriarSubcategoriaModal] = useState(false);
  const [novaSubcategoriaNome, setNovaSubcategoriaNome] = useState('');
  const [salvandoSubcategoria, setSalvandoSubcategoria] = useState(false);

  const [formData, setFormData] = useState({
    nome: '',
    tipo: 'D' as TipoTransacao,
    codigo: '',
    grupo: '',
    cor: '#3B82F6',
    icone: '',
    ativa: true,
    abreviacao: '',
    comentario: '',
  });

  useEffect(() => {
    loadSubcategorias();
    if (isEdit && id) {
      loadCategoria(id);
    }
  }, [isEdit, id]);

  const loadSubcategorias = async () => {
    try {
      // Buscar apenas subcategorias ativas
      const data = await financeiroApi.getSubcategorias({
        page_size: 1000,
        ativa: true  // Filtrar apenas ativas
      });
      // Garantir que IDs sejam strings para comparação
      const subcategorias = (data.results || []).map((sub: any) => ({
        ...sub,
        id: String(sub.id)
      }));
      setTodasSubcategorias(subcategorias);
    } catch (error) {
      console.error('Error loading subcategorias:', error);
    }
  };

  const loadCategoria = async (categoriaId: string) => {
    try {
      setLoading(true);
      const categoria = await financeiroApi.getCategoria(categoriaId);

      if (categoria) {
        setFormData({
          nome: categoria.nome || '',
          tipo: categoria.tipo || 'D',
          codigo: categoria.codigo || '',
          grupo: categoria.grupo || '',
          cor: categoria.cor || '#3B82F6',
          icone: categoria.icone || '',
          ativa: categoria.ativa !== undefined ? categoria.ativa : true,
          abreviacao: categoria.abreviacao || '',
          comentario: categoria.observacoes || '',
        });

        // Marcar subcategorias já vinculadas
        if (categoria.subcategorias && categoria.subcategorias.length > 0) {
          const selecionadas = new Set(categoria.subcategorias.map((sub: any) => String(sub.id)));
          setSubcategoriasSelecionadas(selecionadas);
        }
      }
    } catch (error) {
      console.error('Error loading categoria:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleChange = (e: React.ChangeEvent<HTMLInputElement | HTMLSelectElement | HTMLTextAreaElement>) => {
    const { name, value, type } = e.target;
    const checked = (e.target as HTMLInputElement).checked;

    setFormData(prev => ({
      ...prev,
      [name]: type === 'checkbox' ? checked : value
    }));

    if (errors[name]) {
      setErrors(prev => ({ ...prev, [name]: '' }));
    }
  };

  const handleSubcategoriaToggle = (subcategoriaId: string) => {
    setSubcategoriasSelecionadas(prev => {
      const newSet = new Set(prev);
      if (newSet.has(subcategoriaId)) {
        newSet.delete(subcategoriaId);
      } else {
        newSet.add(subcategoriaId);
      }
      return newSet;
    });
  };

  const handleRemoverSubcategoria = (subcategoriaId: string) => {
    setSubcategoriasSelecionadas(prev => {
      const newSet = new Set(prev);
      newSet.delete(subcategoriaId);
      return newSet;
    });
  };

  const handleCriarSubcategoria = async () => {
    if (!novaSubcategoriaNome.trim()) {
      alert('Por favor, informe o nome da subcategoria');
      return;
    }

    setSalvandoSubcategoria(true);
    try {
      const novaSubcategoria = await financeiroApi.createSubcategoria({
        nome: novaSubcategoriaNome,
        ativa: true
      });

      // Adicionar à lista e selecionar automaticamente (garantir ID como string)
      const subcategoriaComIdString = { ...novaSubcategoria, id: String(novaSubcategoria.id) };
      setTodasSubcategorias(prev => [...prev, subcategoriaComIdString]);
      setSubcategoriasSelecionadas(prev => {
        const newSet = new Set(prev);
        newSet.add(String(novaSubcategoria.id));
        return newSet;
      });

      // Fechar modal e limpar campo
      setShowCriarSubcategoriaModal(false);
      setNovaSubcategoriaNome('');
      alert('Subcategoria criada com sucesso!');
    } catch (error: any) {
      console.error('Error creating subcategoria:', error);
      alert('Erro ao criar subcategoria: ' + (error.message || 'Erro desconhecido'));
    } finally {
      setSalvandoSubcategoria(false);
    }
  };

  // Subcategorias vinculadas (apenas as selecionadas)
  const subcategoriasVinculadas = todasSubcategorias.filter(sub =>
    subcategoriasSelecionadas.has(sub.id)
  );

  // Subcategorias disponíveis para adicionar (não selecionadas)
  const subcategoriasDisponiveis = todasSubcategorias.filter(sub =>
    !subcategoriasSelecionadas.has(sub.id)
  );

  const validateForm = (): boolean => {
    const newErrors: Record<string, string> = {};

    if (!formData.nome.trim()) {
      newErrors.nome = 'Nome é obrigatório';
    }

    if (!formData.tipo) {
      newErrors.tipo = 'Tipo é obrigatório';
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();

    if (!validateForm()) {
      return;
    }

    setSaving(true);
    try {
      // DEBUG: Verificar token
      const storedTokens = localStorage.getItem('authTokens');
      console.log('🔍 DEBUG - localStorage authTokens:', storedTokens ? 'existe' : 'NÃO EXISTE');
      if (storedTokens) {
        const tokenData = JSON.parse(storedTokens);
        console.log('🔍 DEBUG - Token access primeiros 50 chars:', tokenData.access?.substring(0, 50));
      }

      const payload: any = {
        nome: formData.nome,
        tipo: formData.tipo,
        codigo: formData.codigo || `${formData.tipo}-${Date.now()}`,
        grupo: formData.grupo || undefined,
        cor: formData.cor || undefined,
        icone: formData.icone || undefined,
        ativa: formData.ativa,
        categoria_pai: null, // Sempre categoria principal
        subcategorias_ids: Array.from(subcategoriasSelecionadas),
      };

      console.log('Salvando categoria:', payload);

      if (isEdit && id) {
        console.log('Editando categoria ID:', id);
        await financeiroApi.updateCategoria(Number(id), payload);
      } else {
        console.log('Criando nova categoria');
        await financeiroApi.createCategoria(payload);
      }

      navigate('/financeiro/categorias');
    } catch (error: any) {
      console.error('Error saving categoria:', error);
      if (error.message) {
        try {
          const errorObj = JSON.parse(error.message);
          setErrors(errorObj);
          alert('Erro ao salvar: ' + JSON.stringify(errorObj));
        } catch {
          setErrors({ general: 'Erro ao salvar categoria' });
          alert('Erro ao salvar categoria: ' + error.message);
        }
      } else {
        alert('Erro desconhecido ao salvar categoria');
      }
    } finally {
      setSaving(false);
    }
  };

  if (loading) {
    return (
      <div className="flex justify-center items-center py-8">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary-600"></div>
      </div>
    );
  }

  return (
    <div className="space-y-4">
      <div className="mb-4">
        <h1 className="text-base font-bold text-secondary-900">
          {isEdit ? 'Editar Categoria' : 'Nova Categoria'}
        </h1>
        <p className="text-xs text-secondary-600">
          {isEdit ? 'Atualize as informações da categoria e suas subcategorias' : 'Cadastre uma nova categoria financeira'}
        </p>
      </div>

      <form onSubmit={handleSubmit}>
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
          {/* LADO ESQUERDO - Formulário */}
          <div className="space-y-4">
            {/* Nome da Categoria */}
            <div className="bg-white p-4 rounded-lg shadow-sm border border-gray-200">
              <div className="space-y-3">
                <div>
                  <label className="block text-sm font-medium text-secondary-700 mb-1">
                    Nome da Categoria *
                  </label>
                  <input
                    type="text"
                    name="nome"
                    value={formData.nome}
                    onChange={handleChange}
                    className={`input-field ${errors.nome ? 'border-red-300' : ''}`}
                    placeholder="Ex: Adiantamento, Alimentação, Salário"
                    required
                  />
                  {errors.nome && <p className="text-red-600 text-xs mt-1">{errors.nome}</p>}
                </div>

                <div className="grid grid-cols-2 gap-3">
                  <div>
                    <label className="block text-sm font-medium text-secondary-700 mb-1">
                      Tipo *
                    </label>
                    <select
                      name="tipo"
                      value={formData.tipo}
                      onChange={handleChange}
                      className={`input-field ${errors.tipo ? 'border-red-300' : ''}`}
                      required
                    >
                      <option value="R">Receita</option>
                      <option value="D">Despesa</option>
                      <option value="T">Transferência</option>
                    </select>
                    {errors.tipo && <p className="text-red-600 text-xs mt-1">{errors.tipo}</p>}
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-secondary-700 mb-1">
                      Código
                    </label>
                    <input
                      type="text"
                      name="codigo"
                      value={formData.codigo}
                      onChange={handleChange}
                      className="input-field"
                      placeholder="Auto"
                    />
                  </div>
                </div>
              </div>
            </div>

            {/* Subcategorias Vinculadas */}
            <div className="bg-white p-4 rounded-lg shadow-sm border border-gray-200">
              <div className="flex items-center justify-between mb-3">
                <div>
                  <h3 className="text-sm font-semibold text-secondary-900">Subcategorias</h3>
                  <p className="text-xs text-secondary-500">
                    {subcategoriasVinculadas.length} subcategoria(s) vinculada(s)
                  </p>
                </div>
                <button
                  type="button"
                  onClick={() => setShowAdicionarModal(true)}
                  className="px-3 py-1.5 text-xs font-medium text-white bg-primary-600 hover:bg-primary-700 rounded-md transition-colors"
                >
                  + Adicionar
                </button>
              </div>

              {subcategoriasVinculadas.length === 0 ? (
                <div className="border-2 border-dashed border-gray-300 rounded-lg p-6 text-center">
                  <p className="text-xs text-secondary-500">Nenhuma subcategoria vinculada</p>
                  <p className="text-xs text-secondary-400 mt-1">Clique em "Adicionar" para vincular subcategorias</p>
                </div>
              ) : (
                <div className="space-y-1">
                  {subcategoriasVinculadas.map((sub) => (
                    <div
                      key={sub.id}
                      className="flex items-center justify-between px-3 py-2 bg-gray-50 hover:bg-gray-100 rounded border border-gray-200"
                    >
                      <span className="text-sm text-secondary-900">{sub.nome}</span>
                      <button
                        type="button"
                        onClick={() => handleRemoverSubcategoria(sub.id)}
                        className="text-red-600 hover:text-red-800 text-xs font-medium"
                      >
                        Remover
                      </button>
                    </div>
                  ))}
                </div>
              )}
            </div>

            {/* Campos Adicionais */}
            <div className="bg-white p-4 rounded-lg shadow-sm border border-gray-200">
              <h3 className="text-sm font-semibold text-secondary-900 mb-3">Informações Adicionais</h3>

              <div className="space-y-3">
                <div>
                  <label className="block text-sm font-medium text-secondary-700 mb-1">
                    Abreviação
                  </label>
                  <input
                    type="text"
                    name="abreviacao"
                    value={formData.abreviacao}
                    onChange={handleChange}
                    className="input-field"
                    placeholder="Ex: ADIANT, ALIM"
                  />
                </div>

                <div>
                  <label className="block text-sm font-medium text-secondary-700 mb-1">
                    Comentário
                  </label>
                  <textarea
                    name="comentario"
                    value={formData.comentario}
                    onChange={handleChange}
                    rows={3}
                    className="input-field"
                    placeholder="Observações sobre esta categoria"
                  />
                </div>

                <div>
                  <label className="block text-sm font-medium text-secondary-700 mb-1">
                    Grupo de Categoria
                  </label>
                  <select
                    name="grupo"
                    value={formData.grupo}
                    onChange={handleChange}
                    className="input-field"
                  >
                    <option value="">Selecione...</option>
                    <option value="Outras despesas">Outras despesas</option>
                    <option value="Despesas Operacionais">Despesas Operacionais</option>
                    <option value="Despesas Administrativas">Despesas Administrativas</option>
                    <option value="Receitas de Serviços">Receitas de Serviços</option>
                  </select>
                </div>

                <div className="grid grid-cols-2 gap-3">
                  <div>
                    <label className="block text-sm font-medium text-secondary-700 mb-1">
                      Cor
                    </label>
                    <input
                      type="color"
                      name="cor"
                      value={formData.cor}
                      onChange={handleChange}
                      className="h-10 w-full rounded border border-gray-300 cursor-pointer"
                    />
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-secondary-700 mb-1">
                      Ícone
                    </label>
                    <input
                      type="text"
                      name="icone"
                      value={formData.icone}
                      onChange={handleChange}
                      className="input-field"
                      placeholder="💰"
                    />
                  </div>
                </div>

                <div className="flex items-center">
                  <input
                    id="ativa"
                    name="ativa"
                    type="checkbox"
                    checked={formData.ativa}
                    onChange={handleChange}
                    className="h-4 w-4 text-primary-600 focus:ring-primary-500 border-gray-300 rounded"
                  />
                  <label htmlFor="ativa" className="ml-2 block text-sm text-secondary-900">
                    Categoria ativa
                  </label>
                </div>
              </div>
            </div>
          </div>

          {/* LADO DIREITO - Preview/Transações */}
          <div className="space-y-4">
            <div className="bg-white p-4 rounded-lg shadow-sm border border-gray-200">
              <h3 className="text-sm font-semibold text-secondary-900 mb-3">
                Preview da Categoria
              </h3>

              <div className="border border-gray-200 rounded p-4">
                <div className="flex items-center gap-3 mb-4">
                  {formData.icone && <span className="text-2xl">{formData.icone}</span>}
                  <div>
                    <h4 className="font-semibold text-secondary-900">{formData.nome || 'Nome da categoria'}</h4>
                    <p className="text-xs text-secondary-500">
                      Tipo: {formData.tipo === 'R' ? 'Receita' : formData.tipo === 'D' ? 'Despesa' : 'Transferência'}
                    </p>
                  </div>
                </div>

                {subcategoriasSelecionadas.size > 0 && (
                  <div>
                    <p className="text-xs font-medium text-secondary-700 mb-2">Subcategorias:</p>
                    <div className="flex flex-wrap gap-1">
                      {Array.from(subcategoriasSelecionadas).map(subId => {
                        const sub = todasSubcategorias.find(s => s.id === subId);
                        return sub ? (
                          <span
                            key={subId}
                            className="inline-flex items-center px-2 py-1 rounded-full text-xs bg-blue-100 text-blue-800"
                          >
                            {sub.nome}
                          </span>
                        ) : null;
                      })}
                    </div>
                  </div>
                )}

                {formData.grupo && (
                  <div className="mt-3">
                    <p className="text-xs text-secondary-500">Grupo: {formData.grupo}</p>
                  </div>
                )}
              </div>

              {isEdit && (
                <div className="mt-4">
                  <p className="text-xs text-secondary-500">
                    💡 As transações relacionadas a esta categoria serão exibidas aqui em breve
                  </p>
                </div>
              )}
            </div>
          </div>
        </div>

        {/* Actions */}
        <div className="flex justify-end space-x-2 mt-6">
          <button
            type="button"
            onClick={() => navigate('/financeiro/categorias')}
            className="btn-secondary"
            disabled={saving}
          >
            Cancelar
          </button>

          <button
            type="submit"
            disabled={saving}
            className="btn-primary disabled:opacity-50 disabled:cursor-not-allowed"
          >
            {saving ? (
              <div className="flex items-center">
                <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white mr-2"></div>
                Salvando...
              </div>
            ) : (
              isEdit ? 'Atualizar Categoria' : 'Criar Categoria'
            )}
          </button>
        </div>
      </form>

      {/* Modal Adicionar Subcategorias */}
      {showAdicionarModal && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
          <div className="bg-white rounded-lg shadow-xl max-w-md w-full mx-4">
            <div className="px-6 py-4 border-b border-gray-200">
              <h3 className="text-lg font-semibold text-secondary-900">
                Adicionar Subcategorias
              </h3>
              <p className="text-xs text-secondary-500 mt-1">
                Selecione as subcategorias para vincular a esta categoria
              </p>
            </div>

            <div className="px-6 py-4 max-h-96 overflow-y-auto">
              {subcategoriasDisponiveis.length === 0 ? (
                <div className="text-center py-8">
                  <p className="text-xs text-secondary-500">
                    Todas as subcategorias já estão vinculadas
                  </p>
                </div>
              ) : (
                <div className="space-y-1">
                  {subcategoriasDisponiveis.map((sub) => (
                    <label
                      key={sub.id}
                      className="flex items-center px-3 py-2 hover:bg-gray-50 rounded cursor-pointer border border-gray-200"
                    >
                      <input
                        type="checkbox"
                        onChange={() => handleSubcategoriaToggle(sub.id)}
                        className="h-4 w-4 text-primary-600 focus:ring-primary-500 border-gray-300 rounded"
                      />
                      <span className="ml-3 text-sm text-secondary-900">{sub.nome}</span>
                    </label>
                  ))}
                </div>
              )}
            </div>

            <div className="px-6 py-4 border-t border-gray-200 flex justify-between">
              <button
                type="button"
                onClick={() => setShowCriarSubcategoriaModal(true)}
                className="px-3 py-1.5 text-sm font-medium text-primary-700 bg-primary-50 border border-primary-300 rounded-md hover:bg-primary-100"
              >
                + Nova Subcategoria
              </button>
              <button
                type="button"
                onClick={() => setShowAdicionarModal(false)}
                className="px-3 py-1.5 text-sm font-medium text-secondary-700 bg-white border border-gray-300 rounded-md hover:bg-gray-50"
              >
                Fechar
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Modal Criar Nova Subcategoria */}
      {showCriarSubcategoriaModal && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
          <div className="bg-white rounded-lg shadow-xl max-w-md w-full mx-4">
            <div className="px-6 py-4 border-b border-gray-200">
              <h3 className="text-lg font-semibold text-secondary-900">
                Nova Subcategoria
              </h3>
              <p className="text-xs text-secondary-500 mt-1">
                Criar uma nova subcategoria (rótulo)
              </p>
            </div>

            <div className="px-6 py-4">
              <div>
                <label className="block text-sm font-medium text-secondary-700 mb-2">
                  Nome da Subcategoria *
                </label>
                <input
                  type="text"
                  value={novaSubcategoriaNome}
                  onChange={(e) => setNovaSubcategoriaNome(e.target.value)}
                  className="input-field w-full"
                  placeholder="Ex: Transporte, Manutenção, Combustível"
                  autoFocus
                  onKeyPress={(e) => {
                    if (e.key === 'Enter') {
                      handleCriarSubcategoria();
                    }
                  }}
                />
                <p className="text-xs text-secondary-500 mt-1">
                  Subcategorias são rótulos que podem ser vinculados a múltiplas categorias
                </p>
              </div>
            </div>

            <div className="px-6 py-4 border-t border-gray-200 flex justify-end space-x-2">
              <button
                type="button"
                onClick={() => {
                  setShowCriarSubcategoriaModal(false);
                  setNovaSubcategoriaNome('');
                }}
                className="px-3 py-1.5 text-sm font-medium text-secondary-700 bg-white border border-gray-300 rounded-md hover:bg-gray-50"
                disabled={salvandoSubcategoria}
              >
                Cancelar
              </button>
              <button
                type="button"
                onClick={handleCriarSubcategoria}
                disabled={salvandoSubcategoria || !novaSubcategoriaNome.trim()}
                className="px-3 py-1.5 text-sm font-medium text-white bg-primary-600 rounded-md hover:bg-primary-700 disabled:opacity-50 disabled:cursor-not-allowed"
              >
                {salvandoSubcategoria ? (
                  <div className="flex items-center">
                    <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white mr-2"></div>
                    Criando...
                  </div>
                ) : (
                  'Criar Subcategoria'
                )}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default CategoriaForm;
